// AgentIcon — an agent's `icon` as it should be DRAWN, whatever the manifest
// carries.
//
// Stamped agents put an emoji in `icon` ("🔀", "📚"); fleet-built agents often
// put a lucide icon NAME ("search-alert", "shield-check") — the builder skill
// writes what the design named. Rendered as text, a name is a 12-character word
// in a 16-px slot, so on the Agents rail it ran under the agent's title
// (Alex, 2026-09-14: "text overlapping"). Rule: an emoji (or any 1–2 glyph
// string) is shown as text; a lucide name is looked up in lucide's icon map
// (kebab-case → PascalCase, e.g. `search-alert` → SearchAlert); anything
// unknown falls back to the robot.
//
// `emojiOf(icon)` gives the plain-text stand-in for places that can only show
// a string (the chat's pinned-agent chip): the emoji itself, or "🤖".
import React from "react";
import { icons as lucideIcons, Bot } from "lucide-react";

const GLYPH_RE = /^\p{Extended_Pictographic}|^[\p{So}\p{Sk}]/u;

export function isGlyphIcon(icon) {
  if (typeof icon !== "string") return false;
  const s = icon.trim();
  if (!s) return false;
  // Up to two grapheme-ish code points and at least one pictographic symbol.
  return Array.from(s).length <= 3 && GLYPH_RE.test(s);
}

function lucideComponent(name) {
  if (typeof name !== "string") return null;
  const pascal = name
    .trim()
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join("");
  return lucideIcons[pascal] || null;
}

export function emojiOf(icon) {
  return isGlyphIcon(icon) ? icon.trim() : "🤖";
}

export default function AgentIcon({ icon, size = 16, className = "" }) {
  if (isGlyphIcon(icon)) {
    return (
      <span
        className={"inline-flex items-center justify-center leading-none select-none " + className}
        style={{ fontSize: size * 0.95, width: size, height: size }}
        aria-hidden
      >
        {icon.trim()}
      </span>
    );
  }
  const Cmp = lucideComponent(icon) || Bot;
  return <Cmp size={size} className={"shrink-0 " + className} aria-hidden />;
}
